import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';
import { useSystemSettings, useUpdateSystemSettings } from '@/hooks/useSettings';

export default function RatesSettings() {
  const { data: settings, isLoading } = useSystemSettings();
  const updateSettings = useUpdateSystemSettings();

  const [taxRate, setTaxRate] = useState('');
  const [markupRate, setMarkupRate] = useState('');
  const [invoicePrefix, setInvoicePrefix] = useState('');

  useEffect(() => {
    if (settings) {
      setTaxRate(String(Math.round(settings.taxRate * 10000) / 100));
      setMarkupRate(String(Math.round(settings.markupRate * 10000) / 100));
      setInvoicePrefix(settings.invoicePrefix);
    }
  }, [settings]);

  const handleSave = () => {
    const taxDecimal = parseFloat(taxRate) / 100;
    const markupDecimal = parseFloat(markupRate) / 100;

    if (isNaN(taxDecimal) || isNaN(markupDecimal)) return;
    if (taxDecimal < 0 || taxDecimal > 1 || markupDecimal < 0 || markupDecimal > 1) return;

    updateSettings.mutate({
      taxRate: taxDecimal,
      markupRate: markupDecimal,
      invoicePrefix,
    });
  };

  const hasChanges = settings
    ? parseFloat(taxRate) !== Math.round(settings.taxRate * 10000) / 100 ||
      parseFloat(markupRate) !== Math.round(settings.markupRate * 10000) / 100 ||
      invoicePrefix !== settings.invoicePrefix
    : false;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-gray-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Tax & Markup Rates</h2>
        <p className="text-sm text-gray-500 mt-1">
          These default rates are applied to new jobs. Existing jobs retain their own rates.
        </p>
      </div>

      <div className="max-w-md space-y-4">
        <div>
          <label htmlFor="taxRate" className="block text-sm font-medium text-gray-700 mb-1">
            Tax Rate (%)
          </label>
          <p className="text-xs text-gray-500 mb-1">Applied to labor costs</p>
          <input
            id="taxRate"
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={taxRate}
            onChange={(e) => setTaxRate(e.target.value)}
            className="w-32 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div>
          <label htmlFor="markupRate" className="block text-sm font-medium text-gray-700 mb-1">
            Markup Rate (%)
          </label>
          <p className="text-xs text-gray-500 mb-1">Applied to subtotal</p>
          <input
            id="markupRate"
            type="number"
            min="0"
            max="100"
            step="0.1"
            value={markupRate}
            onChange={(e) => setMarkupRate(e.target.value)}
            className="w-32 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        <div className="mt-8 pt-6 border-t border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Invoice Settings</h3>

          <div className="space-y-4">
            <div>
              <label htmlFor="invoicePrefix" className="block text-sm font-medium text-gray-700 mb-1">
                Invoice Number Prefix
              </label>
              <p className="text-xs text-gray-500 mb-1">
                Prefix for generated invoice numbers — the {invoicePrefix || 'INV-'} in{' '}
                {invoicePrefix || 'INV-'}NC-26100-1. The rest comes from the job: its code, then
                that job&apos;s own count of invoices. Cash invoices count separately, as -C1, -C2.
              </p>
              <input
                id="invoicePrefix"
                type="text"
                maxLength={20}
                value={invoicePrefix}
                onChange={(e) => setInvoicePrefix(e.target.value)}
                className="w-32 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <button
          onClick={handleSave}
          disabled={!hasChanges || updateSettings.isPending}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-md hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {updateSettings.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
          Save
        </button>
      </div>
    </div>
  );
}
